import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Save, Eye } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const EMPTY = {
  title: "",
  subtitle: "",
  category: "",
  excerpt: "",
  hero_image: "",
  tags: "",
  read_time_minutes: 6,
  content_markdown: "## Executive summary\n\nStart writing…",
  published: true,
  slug: "",
};

export default function AdminEditorPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    api
      .get("/admin/articles")
      .then((r) => {
        const a = r.data.find((x) => x.id === id);
        if (!a) {
          toast.error("Article not found");
          navigate("/admin");
          return;
        }
        setForm({
          ...a,
          tags: Array.isArray(a.tags) ? a.tags.join(", ") : "",
        });
      })
      .catch((err) =>
        toast.error(formatApiErrorDetail(err.response?.data?.detail))
      )
      .finally(() => setLoading(false));
  }, [id, isEdit, navigate]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title || !form.content_markdown) {
      toast.error("Title and content are required");
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title,
      subtitle: form.subtitle,
      category: form.category,
      excerpt: form.excerpt,
      hero_image: form.hero_image,
      tags: (form.tags || "")
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      read_time_minutes: Number(form.read_time_minutes) || 5,
      content_markdown: form.content_markdown,
      published: !!form.published,
    };
    try {
      if (isEdit) {
        await api.put(`/admin/articles/${id}`, payload);
        toast.success("Article updated");
      } else {
        if (form.slug) payload.slug = form.slug;
        await api.post("/admin/articles", payload);
        toast.success("Article published");
      }
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <p data-testid="editor-loading" className="eyebrow text-ink-faint">
          Loading…
        </p>
      </div>
    );
  }

  return (
    <div data-testid="page-admin-editor" className="min-h-screen bg-paper">
      <header className="border-b border-rule bg-cream sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between gap-4">
          <Link
            to="/admin"
            data-testid="link-back-admin"
            className="eyebrow text-navy hover:text-gold flex items-center gap-2 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Editor home
          </Link>
          <p className="font-serif text-lg text-navy hidden md:block">
            {isEdit ? "Edit article" : "New article"}
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowPreview((v) => !v)}
              data-testid="btn-toggle-preview"
              className="eyebrow text-navy hover:text-gold flex items-center gap-2 transition-colors"
            >
              <Eye className="w-4 h-4" />
              {showPreview ? "Hide preview" : "Preview"}
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              data-testid="btn-save-article"
              className="bg-navy hover:bg-navy/90 text-paper px-4 py-2 flex items-center gap-2 disabled:opacity-50 transition-colors"
            >
              <Save className="w-4 h-4" />
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <form
          onSubmit={handleSave}
          className={`space-y-5 ${
            showPreview ? "lg:col-span-6" : "lg:col-span-12"
          }`}
        >
          <Field label="Title" required>
            <input
              data-testid="editor-title"
              type="text"
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              className={inputCls}
              placeholder="An action-oriented title that IS the message"
            />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Category">
              <input
                data-testid="editor-category"
                type="text"
                value={form.category}
                onChange={(e) => update("category", e.target.value)}
                className={inputCls}
                placeholder="e.g. Marketplaces & E-commerce"
              />
            </Field>
            <Field label="Read time (minutes)">
              <input
                data-testid="editor-readtime"
                type="number"
                min={1}
                value={form.read_time_minutes}
                onChange={(e) =>
                  update("read_time_minutes", Number(e.target.value))
                }
                className={inputCls}
              />
            </Field>
          </div>

          <Field label="Subtitle">
            <textarea
              data-testid="editor-subtitle"
              rows={2}
              value={form.subtitle}
              onChange={(e) => update("subtitle", e.target.value)}
              className={inputCls}
              placeholder="A sentence-long framing of the argument"
            />
          </Field>

          <Field label="Excerpt (for cards & previews)">
            <textarea
              data-testid="editor-excerpt"
              rows={3}
              value={form.excerpt}
              onChange={(e) => update("excerpt", e.target.value)}
              className={inputCls}
            />
          </Field>

          <Field label="Hero image URL">
            <input
              data-testid="editor-hero"
              type="url"
              value={form.hero_image}
              onChange={(e) => update("hero_image", e.target.value)}
              className={inputCls}
              placeholder="https://…"
            />
          </Field>

          <Field label="Tags (comma-separated)">
            <input
              data-testid="editor-tags"
              type="text"
              value={form.tags}
              onChange={(e) => update("tags", e.target.value)}
              className={inputCls}
              placeholder="Strategy, AI, ESG"
            />
          </Field>

          {!isEdit && (
            <Field label="Custom slug (optional)">
              <input
                data-testid="editor-slug"
                type="text"
                value={form.slug}
                onChange={(e) => update("slug", e.target.value)}
                className={inputCls}
                placeholder="auto-generated from title if left blank"
              />
            </Field>
          )}

          <Field label="Content · Markdown (supports headings, lists, tables, blockquotes)" required>
            <textarea
              data-testid="editor-content"
              rows={22}
              value={form.content_markdown}
              onChange={(e) => update("content_markdown", e.target.value)}
              className={`${inputCls} font-mono text-sm leading-relaxed`}
            />
          </Field>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              data-testid="editor-published"
              type="checkbox"
              checked={form.published}
              onChange={(e) => update("published", e.target.checked)}
              className="w-4 h-4 accent-navy"
            />
            <span className="text-sm text-navy">
              Publish immediately (uncheck to save as draft)
            </span>
          </label>
        </form>

        {showPreview && (
          <aside
            data-testid="editor-preview"
            className="lg:col-span-6 border border-rule p-6 lg:p-10 bg-cream max-h-[calc(100vh-8rem)] overflow-y-auto sticky top-24"
          >
            <p className="eyebrow text-gold mb-3">Preview</p>
            {form.category && (
              <p className="eyebrow text-ink-muted mb-3">{form.category}</p>
            )}
            <h1 className="font-serif text-3xl md:text-4xl text-navy leading-tight mb-4">
              {form.title || "Untitled"}
            </h1>
            {form.subtitle && (
              <p className="text-ink-muted mb-6">{form.subtitle}</p>
            )}
            <div className="prose-editorial">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {form.content_markdown || ""}
              </ReactMarkdown>
            </div>
          </aside>
        )}
      </main>
    </div>
  );
}

const inputCls =
  "w-full border border-rule focus:border-navy outline-none px-4 py-3 bg-paper text-navy transition-colors placeholder:text-ink-faint";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="eyebrow text-ink-muted block mb-2">
        {label} {required && <span className="text-gold">*</span>}
      </span>
      {children}
    </label>
  );
}
